import { useState, useEffect } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Buildings, CheckCircle, FloppyDisk } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ClientPublicRegister({ registrationToken }) {
  const [valid, setValid] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    razon_social: "", nombre_comercial: "", rfc: "",
    calle: "", colonia: "", ciudad: "", estado: "", cp: "",
    compras_nombre: "", compras_puesto: "", compras_email: "", compras_telefono: "",
    cxp_nombre: "", cxp_email_general: "", cxp_email_cfdi: "", cxp_email_rep: "", cxp_telefono: "",
    banco: "", cuenta: "", clabe: "", moneda: "MXN",
    uso_cfdi: "", forma_pago: "", metodo_pago: "", notes: "",
  });

  useEffect(() => {
    axios.get(`${API}/public/client-register/${registrationToken}`)
      .then(() => setValid(true))
      .catch(() => setValid(false));
  }, [registrationToken]);

  const set = (f, v) => setForm({ ...form, [f]: v });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.razon_social.trim()) { toast.error("Razon Social es requerida"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/public/client-register/${registrationToken}`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar");
    } finally { setSaving(false); }
  };

  if (valid === null) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Validando enlace...</p>
      </div>
    );
  }

  if (valid === false) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white rounded-xl border p-8 max-w-md text-center">
          <h1 className="text-xl font-bold text-red-600 mb-2">Enlace Invalido</h1>
          <p className="text-slate-600">Este enlace de registro ya fue utilizado o no es valido. Solicita un nuevo enlace a tu contacto.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white rounded-xl border p-8 max-w-md text-center">
          <CheckCircle size={48} className="text-emerald-500 mx-auto mb-4" weight="duotone" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Registro Exitoso</h1>
          <p className="text-slate-600">Tu informacion ha sido enviada correctamente. Nos pondremos en contacto contigo pronto.</p>
        </div>
      </div>
    );
  }

  const InputField = ({ label, field, type = "text", placeholder = "", required = false, colSpan = "" }) => (
    <div className={colSpan}>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}{required && " *"}</label>
      <input type={type} value={form[field]} onChange={e => set(field, e.target.value)} placeholder={placeholder}
        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
        data-testid={`pub-${field}`} required={required} />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4" data-testid="public-client-register">
      <Toaster position="top-center" richColors />
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
              <Buildings size={24} className="text-white" weight="duotone" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Registro de Cliente</h1>
          </div>
          <p className="text-slate-500 text-sm">Completa la siguiente informacion para darte de alta como cliente.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Datos Generales */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4">Datos Generales</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InputField label="Razon Social" field="razon_social" required />
              <InputField label="Nombre Comercial" field="nombre_comercial" />
              <InputField label="RFC" field="rfc" placeholder="XAXX010101000" />
            </div>
          </div>

          {/* Direccion */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4">Direccion</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InputField label="Calle y Numero" field="calle" colSpan="md:col-span-2" />
              <InputField label="Colonia" field="colonia" />
              <InputField label="Ciudad" field="ciudad" />
              <InputField label="Estado" field="estado" />
              <InputField label="Codigo Postal" field="cp" />
            </div>
          </div>

          {/* Contacto de Compras */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4">Contacto de Compras</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label="Nombre" field="compras_nombre" />
              <InputField label="Puesto" field="compras_puesto" />
              <InputField label="Email" field="compras_email" type="email" />
              <InputField label="Telefono" field="compras_telefono" />
            </div>
          </div>

          {/* Contacto Cuentas por Pagar */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4">Contacto Cuentas por Pagar</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InputField label="Nombre" field="cxp_nombre" />
              <InputField label="Email General" field="cxp_email_general" type="email" />
              <InputField label="Email CFDI" field="cxp_email_cfdi" type="email" placeholder="facturacion@empresa.com" />
              <InputField label="Email REP" field="cxp_email_rep" type="email" />
              <InputField label="Telefono" field="cxp_telefono" />
            </div>
          </div>

          {/* Datos Bancarios */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4">Datos Bancarios</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <InputField label="Banco" field="banco" />
              <InputField label="Numero de Cuenta" field="cuenta" />
              <InputField label="CLABE Interbancaria" field="clabe" placeholder="18 digitos" />
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Moneda</label>
                <select value={form.moneda} onChange={e => set("moneda", e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" data-testid="pub-moneda">
                  <option value="MXN">Pesos (MXN)</option>
                  <option value="USD">Dolares (USD)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Datos de Factura */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4">Datos de Facturacion</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InputField label="Uso CFDI" field="uso_cfdi" placeholder="Ej: G03 - Gastos en general" />
              <InputField label="Forma de Pago" field="forma_pago" placeholder="Ej: 03 - Transferencia" />
              <InputField label="Metodo de Pago" field="metodo_pago" placeholder="Ej: PPD, PUE" />
            </div>
          </div>

          {/* Notas */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <label className="block text-xs font-medium text-slate-600 mb-1">Notas adicionales</label>
            <textarea value={form.notes} onChange={e => set("notes", e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm min-h-[80px] focus:ring-2 focus:ring-blue-500 outline-none resize-y"
              placeholder="Informacion adicional que quieras compartir..." data-testid="pub-notes" />
          </div>

          {/* Submit */}
          <div className="text-center">
            <button type="submit" disabled={saving}
              className="px-8 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 inline-flex items-center gap-2 shadow-lg shadow-blue-600/20"
              data-testid="pub-submit">
              <FloppyDisk size={18} />
              {saving ? "Enviando..." : "Enviar Registro"}
            </button>
          </div>
        </form>

        <p className="text-center text-xs text-slate-400 mt-8">Powered by MaxTicket</p>
      </div>
    </div>
  );
}
